import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Type, AlignLeft, ChevronDown, CheckSquare, Hash } from 'lucide-react'

interface FieldTypeOption {
  id: string
  label: string
  icon: React.ReactNode
  description: string
}

const FIELD_TYPES: FieldTypeOption[] = [
  {
    id: 'text',
    label: 'Text',
    icon: <Type className="w-5 h-5" />,
    description: 'Short text input'
  },
  {
    id: 'textarea',
    label: 'Long Text',
    icon: <AlignLeft className="w-5 h-5" />,
    description: 'Multi-line text'
  },
  {
    id: 'number',
    label: 'Number',
    icon: <Hash className="w-5 h-5" />,
    description: 'Numeric input'
  },
  {
    id: 'select',
    label: 'Dropdown',
    icon: <ChevronDown className="w-5 h-5" />,
    description: 'Choose one option'
  },
  {
    id: 'checkbox',
    label: 'Checkbox',
    icon: <CheckSquare className="w-5 h-5" />,
    description: 'Yes/no option'
  }
]

interface AddFieldMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onSelectField: (fieldType: string) => void
}

/**
 * Popup menu for selecting field type to add
 *
 * Pattern from email builder's BlocksMenu
 */
export default function AddFieldMenu({ anchorEl, open, onClose, onSelectField }: AddFieldMenuProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (anchorEl && open) {
      const rect = anchorEl.getBoundingClientRect()
      const menuWidth = 500 // Approximate menu width
      const viewportWidth = window.innerWidth

      // Calculate left position, ensuring menu stays within viewport
      let left = rect.left + rect.width / 2 + window.scrollX

      // If menu would go off right edge, align to right edge with padding
      if (left + menuWidth / 2 > viewportWidth - 20) {
        left = viewportWidth - menuWidth / 2 - 20 + window.scrollX
      }

      // If menu would go off left edge, align to left edge with padding
      if (left - menuWidth / 2 < 20) {
        left = menuWidth / 2 + 20 + window.scrollX
      }

      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: left,
      })
    }
  }, [anchorEl, open])

  if (!open) {
    return null
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />

      {/* Menu */}
      <div
        className="fixed z-50 bg-popover border rounded-lg shadow-xl p-4 w-[480px]"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          transform: 'translateX(-50%)',
        }}
      >
        <div className="text-xs font-semibold text-foreground mb-3">
          Add Field
        </div>

        <div className="grid grid-cols-2 gap-2">
          {FIELD_TYPES.map((fieldType) => (
            <button
              key={fieldType.id}
              onClick={() => onSelectField(fieldType.id)}
              className="flex items-start gap-3 p-3 hover:bg-accent rounded-lg transition-colors text-left group border border-border hover:border-primary"
            >
              <div className="text-muted-foreground group-hover:text-primary transition-colors mt-0.5">
                {fieldType.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium group-hover:text-primary transition-colors">
                  {fieldType.label}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fieldType.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body
  )
}
